import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import beTwMerge from '@/utils/beTwMerge';
import ResponsiveImage, { ResponsiveImageSet } from '@/utils/ResponsiveImage';
import { getCtaMaxWidth, isRichTextEmpty, resetCtaWidth, setCtaSameWidth } from '@/utils/builderUtils';
import Button from '@/components/homepage/Button/Button';
import { Action } from '../LargeHeroBanner/LargeHeroBanner';
import DropdownButton from '../DropdownButton/DropdownButton';

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    column-gap: 8px;
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'left1 right1'
      'left2 right2'
      'left3 right3';
  }

  h3:first-child {
    margin-top: 0px;
  }

  .grid-item {
    min-width: 0;
  }

  .left1,
  .right1 {
    grid-area: auto;
  }
  .left2,
  .right2 {
    grid-area: auto;
  }
  .left3,
  .right3 {
    grid-area: auto;
  }
  .left4,
  .right4 {
    grid-area: auto;
  }

  @media (min-width: 768px) {
    .left1 {
      grid-area: left1;
    }
    .right1 {
      grid-area: right1;
    }
    .left2 {
      grid-area: left2;
    }
    .right2 {
      grid-area: right2;
    }
    .left3 {
      grid-area: left3;
    }
    .right3 {
      grid-area: right3;
    }
    .left4 {
      grid-area: left4;
    }
    .right4 {
      grid-area: right4;
    }
  }
`;

interface ITile {
  isVideo: boolean;
  videoSrc?: string;
  videoThumbnail: string;
  loop: boolean;
  controls: boolean;
  autoPlay: boolean;
  imgSrc: string;
  imgAlt: string;
  imgLink?: string;
  optionalHeader?: string;
  optionalSubHeader?: string;
  ctas?: (Action & { link: string })[];
}

interface IDoubleImageTileProps {
  isSetMaxWidth?: boolean;
  textBoxPosition?: string;
  tiles: ITile[];
}

const createTilePairs = (tiles: ITile[]): ITile[][] =>
  tiles.reduce((pairs: ITile[][], currentTile, index) => {
    // For every even index, start a new pair
    if (index % 2 === 0) {
      pairs.push([currentTile]);
    } else {
      // For every odd index, add the tile to the last pair
      pairs[pairs.length - 1].push(currentTile);
    }
    return pairs;
  }, []);

export default function DoubleImageTile({
  isSetMaxWidth = true,
  textBoxPosition = 'left',
  tiles,
}: IDoubleImageTileProps) {
  const leftTextRef = useRef<any>(null);
  const RightTextRef = useRef<any>(null);
  const leftButtonRefs = useRef<any[]>([]);
  const RightButtonRefs = useRef<any[]>([]);
  const shouldAddMarginToCtas =
    !tiles[0]?.optionalHeader &&
    isRichTextEmpty(tiles[0]?.optionalSubHeader) &&
    !tiles[1]?.optionalHeader &&
    isRichTextEmpty(tiles[1]?.optionalSubHeader);

  const moreDescriptiveAriaLabel = (title: string | undefined, href: string | undefined, label: string): string => {
    if (label && (label.toLowerCase().trim() === 'shop now' || label.toLowerCase().trim() === 'learn more')) {
      if (title) {
        return `${title} - ${label}`;
      }

      return `${label} on ${href?.replace(/\/$/, '').split('/').pop()?.replace(/-/g, ' ')}}`;
    }
    return label || '';
  };

  const tilesCopy = tiles.slice(0, 6);

  const tilePairs = createTilePairs(tilesCopy);

  const imageSrcs = (tile): ResponsiveImageSet => [
    { src: tile.imgSrc || '', size: { width: 735, height: 556, aspectRatio: 169 / 128 }, breakpoint: 320 },
    { src: tile.imgSrc || '', size: { width: 991, height: 751, aspectRatio: 169 / 128 }, breakpoint: 767 },
    { src: tile.imgSrc || '', size: { width: 720, height: 545, aspectRatio: 169 / 128 }, breakpoint: 1023 },
  ];
  useEffect(() => {
    if (leftButtonRefs.current.length + RightButtonRefs.current.length < 2) return;

    const resizeWidth = () => {
      const resetWidth = window.innerWidth < 1024 ? '100%' : 'auto';
      resetCtaWidth(leftButtonRefs, resetWidth);
      resetCtaWidth(RightButtonRefs, resetWidth);

      if (window.innerWidth < 1024) return;

      const leftButtonMaxWidth = getCtaMaxWidth(leftButtonRefs);
      const rightButtonMaxWidth = getCtaMaxWidth(RightButtonRefs);

      const maxWidth = rightButtonMaxWidth > leftButtonMaxWidth ? rightButtonMaxWidth : leftButtonMaxWidth;

      setCtaSameWidth(leftButtonRefs, maxWidth);
      setCtaSameWidth(RightButtonRefs, maxWidth);
    };
    resizeWidth();
    window.addEventListener('resize', resizeWidth);
    return () => {
      window.removeEventListener('resize', resizeWidth);
    };
  }, [leftButtonRefs, RightButtonRefs]);

  useEffect(() => {
    const resizeHeight = () => {
      if (!leftTextRef || !RightTextRef) return;
      leftTextRef.current.style.height = 'auto';
      RightTextRef.current.style.height = 'auto';
      if (window.innerWidth < 768) return;

      const textBoxHeight = leftTextRef?.current?.offsetHeight || 0;
      const textBox2Height = RightTextRef?.current?.offsetHeight || 0;
      const maxHeight = textBox2Height > textBoxHeight ? textBox2Height : textBoxHeight;
      if (maxHeight) {
        leftTextRef.current.style.height = `${maxHeight}px`;
        RightTextRef.current.style.height = `${maxHeight}px`;
      }
    };
    resizeHeight();
    window.addEventListener('resize', resizeHeight);
    return () => {
      window.removeEventListener('resize', resizeHeight);
    };
  }, [leftTextRef, RightTextRef]);

  const media = tile => (
    <>
      {tile.isVideo && tile.videoSrc && (
        <video
          controls={tile.controls}
          muted
          autoPlay={tile.autoPlay}
          playsInline
          poster={tile.videoThumbnail || ''}
          loop={tile.loop}
          className="tw-w-full tw-aspect-[169/128] tw-object-cover"
          preload="auto"
        >
          <source src={tile.videoSrc} type="video/mp4" />
        </video>
      )}
      {!tile.isVideo &&
        tile.imgSrc &&
        (tile.imgLink ? (
          <div className="tw-aspect-[169/128] tw-w-full tw-h-full md:tw-h-auto lg:tw-h-auto">
            <a className="tw-w-full tw-h-full md:tw-h-auto lg:tw-h-auto" href={tile.imgLink}>
              <ResponsiveImage
                sources={imageSrcs(tile)}
                alt={tile.imgAlt || tile.optionalHeader}
                className="tw-w-full tw-h-full md:tw-h-auto lg:tw-h-auto tw-aspect-[169/128] tw-object-cover"
              />
            </a>
          </div>
        ) : (
          <div className="tw-aspect-[169/128] tw-w-full tw-h-full md:tw-h-auto lg:tw-h-auto">
            <ResponsiveImage
              sources={imageSrcs(tile)}
              alt={tile.imgAlt || tile.optionalHeader}
              className="tw-w-full tw-h-full md:tw-h-auto lg:tw-h-auto tw-aspect-[169/128] tw-object-cover"
            />
          </div>
        ))}
    </>
  );

  const header = tile =>
    tile.optionalHeader && tile.optionalHeader !== '' && <h2 className="first:tw-my-0">{tile.optionalHeader}</h2>;

  const subHeader = tile =>
    !isRichTextEmpty(tile?.optionalSubHeader) && (
      <div
        className={beTwMerge(
          'tw-font-book [&_p]:tw-text-[16px]',
          tile.optionalHeader && tile.optionalHeader !== '' && 'tw-mt-1',
          tile?.ctas?.length !== 0 && 'tw-mb-6',
          '[&>p:last-child]:!tw-mb-0'
        )}
        dangerouslySetInnerHTML={{ __html: tile?.optionalSubHeader || '' }}
      />
    );

  const ctas = (tile, buttonRefs) =>
    tile?.ctas?.length !== 0 && (
      <div
        className={beTwMerge(
          'tw-flex tw-flex-col tw-w-full tw-gap-2 tw-h-fit tw-justify-start tw-flex-[1] large:tw-items-start',
          isTextCenter && 'tw-justify-center',
          shouldAddMarginToCtas && 'lg:tw-mt-[24px]'
        )}
      >
        {tile.ctas?.slice(0, 2).map((action, i) => {
          const { label, color, variant } = action;

          if (action.actions) {
            const { actions: subActions } = action;
            return (
              <DropdownButton
                key={i}
                label={label}
                options={subActions}
                color={color}
                type={variant}
                btnRef={(el: any) => {
                  buttonRefs.current[i] = el;
                }}
                className={beTwMerge(
                  `tw-font-avenirmedium tw-max-w-none [&>button]:tw-max-w-none [&>button]:tw-w-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:large:tw-px-[73px] [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis [&>button]:tw-h-full tw-h-[48px] md:tw-h-[44px]`
                )}
              />
            );
          }
          const { onClick, href, target } = action;
          return (
            <div className="tw-h-[48px] md:tw-h-[44px]">
              <Button
                className={beTwMerge(
                  `tw-font-avenirmedium tw-max-w-none tw-whitespace-nowrap tw-text-ellipsis tw-overflow-hidden tw-h-[48px] tw-px-[73px] md:tw-h-[44px] md:tw-w-full`
                )}
                key={i}
                ariaLabel={moreDescriptiveAriaLabel(tile?.optionalHeader, href, label)}
                label={label}
                color={color}
                type={variant}
                href={href}
                ref={(el: any) => {
                  buttonRefs.current[i] = el;
                }}
                target={target}
                onClick={onClick}
              />
            </div>
          );
        })}
      </div>
    );
  const isTextCenter = textBoxPosition === 'center';

  return (
    <div
      className={`tw-flex tw-flex-col tw-gap-4 tw-mx-4 lg:tw-mx-8 ${isSetMaxWidth ? 'tw-max-w-[1440px] min-[1504px]:tw-mx-auto' : ''}`}
    >
      {tilePairs.map((pair, index) => (
        <GridContainer key={index}>
          <div className="grid-item left1">{media(pair[0])}</div>
          <div
            className={`tw-flex tw-flex-col tw-mt-4 ${isTextCenter ? 'large:tw-items-center tw-text-center' : 'tw-items-left'}`}
          >
            <div className="grid-item left2" ref={leftTextRef}>
              {header(pair[0])}
              {subHeader(pair[0])}
            </div>
            <div className={beTwMerge('grid-item left3', pair[1] && 'tw-mb-8 md:tw-mb-0')}>
              {ctas(pair[0], leftButtonRefs)}
            </div>
          </div>
          <div className="grid-item right1">{pair[1] && media(pair[1])}</div>
          <div>
            <div
              className={`tw-flex tw-flex-col tw-mt-4 ${isTextCenter ? 'large:tw-items-center tw-text-center' : 'tw-items-left'}`}
            >
              <div className="grid-item right2" ref={RightTextRef}>
                {pair[1] && header(pair[1])}
                {pair[1] && subHeader(pair[1])}
              </div>
              <div className="grid-item right3">{pair[1] && ctas(pair[1], RightButtonRefs)}</div>
            </div>
          </div>
        </GridContainer>
      ))}
    </div>
  );
}
